/*
  Make It Louder — reliable removal of sold-out / stuck cart lines.

  Problem: Shopify's /cart/change.js sometimes refuses to remove a line whose
  product is sold out (it returns "The product 'X' is already sold out." and
  leaves the line in place). Dawn's CartItems.updateQuantity treats that as an
  error, reverts the input and never re-renders — so the item can't be removed
  from either the cart drawer or the cart page.

  Fix: patch CartItems.updateQuantity so that any removal (quantity === 0) goes
  through a robust, verified remover:
    1) change.js by line key
    2) update.js by line key
    3) rebuild the cart without the target line (guaranteed)
  Each step is verified against /cart.js. CartDrawerItems extends CartItems, so
  patching the prototype fixes both surfaces at once.

  Load this AFTER cart.js on any template that renders the cart (cart page +
  wherever the drawer lives). Including it twice is safe.
*/
(function () {
  if (window.__milCartRemoveFix) return;
  window.__milCartRemoveFix = true;

  var R = (window.routes || {});
  var CHANGE_URL = R.cart_change_url || '/cart/change.js';
  var UPDATE_URL = R.cart_update_url || '/cart/update.js';
  var ADD_URL = R.cart_add_url || '/cart/add.js';
  var CLEAR_URL = R.cart_clear_url || '/cart/clear.js';
  var CART_URL = R.cart_url || '/cart';
  var CART_JS = '/cart.js';

  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  function postJSON(url, payload) {
    return fetch(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload || {}) })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .catch(function () { return null; });
  }

  function getCart() {
    return fetch(CART_JS, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function cartHasKey(cart, key) {
    return !!(cart && cart.items && cart.items.some(function (it) { return it.key === key; }));
  }

  // Guaranteed removal of the line identified by lineKey (falls back to line index).
  function robustRemove(lineKey, line) {
    return getCart().then(function (cart) {
      // Resolve the key from the line index if we didn't get one from the DOM.
      if (!lineKey && cart && cart.items && line) {
        var byIndex = cart.items[Number(line) - 1];
        if (byIndex) lineKey = byIndex.key;
      }

      // No key at all: best-effort change by line number, then report.
      if (!lineKey) {
        return postJSON(CHANGE_URL, { line: Number(line), quantity: 0 }).then(getCart);
      }

      // 1) change.js by key
      return postJSON(CHANGE_URL, { id: lineKey, quantity: 0 })
        .then(getCart)
        .then(function (c1) {
          if (!cartHasKey(c1, lineKey)) return c1;
          // 2) update.js by key
          var updates = {};
          updates[lineKey] = 0;
          return postJSON(UPDATE_URL, { updates: updates })
            .then(getCart)
            .then(function (c2) {
              if (!cartHasKey(c2, lineKey)) return c2;
              // 3) rebuild the cart without the sold-out line
              return rebuildWithout(c2 || cart, lineKey);
            });
        });
    });
  }

  // Clear the cart and re-add every kept line (preserving quantity, properties,
  // selling plan, note and attributes). The sold-out line is simply not re-added.
  function rebuildWithout(cart, lineKey) {
    // Safety: never clear the cart if we couldn't read a valid one.
    if (!cart || !cart.items || !cart.items.length) {
      return getCart();
    }

    var note = cart && cart.note;
    var attributes = (cart && cart.attributes) || {};
    var keep = ((cart && cart.items) || []).filter(function (it) {
      return it.key !== lineKey && it.quantity > 0;
    });

    return postJSON(CLEAR_URL, {}).then(function () {
      var chain = Promise.resolve();
      keep.forEach(function (it) {
        chain = chain.then(function () {
          var payload = { id: it.id, quantity: it.quantity };
          if (it.properties && Object.keys(it.properties).length) payload.properties = it.properties;
          if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
            payload.selling_plan = it.selling_plan_allocation.selling_plan.id;
          }
          // Best effort: if one item can't be re-added, keep going.
          return postJSON(ADD_URL, { items: [payload] });
        });
      });
      return chain
        .then(function () { if (note) return postJSON(UPDATE_URL, { note: note }); })
        .then(function () {
          if (attributes && Object.keys(attributes).length) return postJSON(UPDATE_URL, { attributes: attributes });
        })
        .then(getCart);
    });
  }

  function reloadHere() {
    window.location.reload();
  }

  // Refresh the UI after removal. When the drawer is open, re-render it in place
  // with FRESH sections (the sold-out line is already gone, so this update call
  // is validation-safe) — never redirect to the cart page. Otherwise reload,
  // which is correct for the cart page and any other surface.
  function refreshUI() {
    var drawer = document.querySelector('cart-drawer');
    var drawerOpen = drawer && drawer.classList.contains('active') && typeof drawer.renderContents === 'function';

    if (drawerOpen) {
      return postJSON(UPDATE_URL, {
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }).then(function (state) {
        if (state && state.sections) {
          try {
            // Re-renders #CartDrawer (items + footer + subtotal + drawer count)
            // and the header cart bubble in one pass.
            drawer.renderContents(state);
            return;
          } catch (e) {}
        }
        reloadHere();
      });
    }

    return Promise.resolve(reloadHere());
  }

  function resolveLineKey(el, line) {
    var candidates = [
      document.getElementById('CartDrawer-Remove-' + line),
      document.getElementById('Remove-' + line),
      document.getElementById('Drawer-quantity-' + line),
      document.getElementById('Quantity-' + line),
    ];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && candidates[i].dataset && candidates[i].dataset.lineKey) {
        return candidates[i].dataset.lineKey;
      }
    }
    return null;
  }

  function handleRemove(cartItemsEl, line) {
    if (cartItemsEl && typeof cartItemsEl.enableLoading === 'function') {
      try { cartItemsEl.enableLoading(line); } catch (e) {}
    }
    var lineKey = resolveLineKey(cartItemsEl, line);

    robustRemove(lineKey, line)
      .then(function () { return refreshUI(); })
      .catch(function () {
        // Last resort: reload the current page (never jump to the cart page).
        reloadHere();
      })
      .finally(function () {
        if (cartItemsEl && typeof cartItemsEl.disableLoading === 'function') {
          try { cartItemsEl.disableLoading(line); } catch (e) {}
        }
      });
  }

  function applyPatch() {
    var Ctor = window.customElements && window.customElements.get('cart-items');
    if (!Ctor || !Ctor.prototype) return false;
    if (Ctor.prototype.__milRemovePatched) return true;

    var original = Ctor.prototype.updateQuantity;
    Ctor.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
      if (Number(quantity) === 0) {
        handleRemove(this, line);
        return;
      }
      return original.call(this, line, quantity, event, name, variantId);
    };
    Ctor.prototype.__milRemovePatched = true;
    return true;
  }

  // cart.js is deferred; poll briefly until <cart-items> is defined, then patch.
  if (!applyPatch()) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (applyPatch() || tries > 100) clearInterval(timer);
    }, 50);
  }
})();